import { Component } from "@theme/component";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
  SubscriptionOpenEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { CustomSelect } from "@theme/custom-select";
import { findPairedFeeRow, silentCartChange, reconcileFeeLines, reorderFeeRows } from "@theme/bcrs-fee";
import { track, ANALYTICS_EVENTS, textParam } from "@theme/analytics";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /**
   * Removals awaiting server confirmation, keyed by line number.
   *
   * The analytics payload has to be read while the row is still in the DOM, but
   * it must only be reported once /cart/change.js confirms the removal — hence
   * capture here, emit in updateQuantity's response handler. Save-for-later
   * calls updateQuantity directly without going through onLineItemRemove, so it
   * never lands in this map and is never counted as a removal.
   * @type {Map<number, Object>}
   */
  #pendingRemovals = new Map();

  /**
   * Swaps a line's subscription frequency (selling plan) in place when an editable
   * frequency <custom-select> changes. The current quantity is sent too, because a
   * selling-plan-only /cart/change.js resets the line quantity to 1.
   * @param {Event} event
   */
  #onFrequencyChange = event => {
    const select = event.target;
    if (!(select instanceof CustomSelect) || !select.hasAttribute("data-cart-frequency")) return;

    const line = Number(select.dataset.line);
    const sellingPlan = select.value;
    if (!line || !sellingPlan) return;

    this.updateQuantity({
      line,
      quantity: Number(select.dataset.quantity) || 1,
      action: "change",
      sellingPlan,
    });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener("change", this.#onFrequencyChange);

    // BCRS fee: group fee rows under their parent line on first render.
    reorderFeeRows(this);
  }

  /**
   * BCRS fee: reconcile fee lines against their parents (quantity drift,
   * orphans). When a server-side fix was applied, re-render the section once
   * so the display reflects it — the refreshed render only regroups, so this
   * cannot loop.
   */
  #reconcileFees() {
    reconcileFeeLines(this)
      .then(changed => {
        if (!changed) return;
        return sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => reorderFeeRows(this));
      })
      .catch(e => console.warn("[bcrs-fee] Fee reconciliation failed", e));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener("change", this.#onFrequencyChange);
  }

  /**
   * Opens the global subscription drawer for an OTP cart line so the shopper can convert
   * it to a subscription. Wired from the line's "Subscribe & Save" CTA via
   * `on:click="/openSubscription"`; reads the trigger's data and announces intent through
   * SubscriptionOpenEvent (the drawer listens — see assets/subscription-drawer.js).
   * @param {Event} event - The click event; its target carries the line data attributes.
   */
  openSubscription(event) {
    const trigger = event.target instanceof Element ? event.target.closest("[data-handle]") : null;
    if (!(trigger instanceof HTMLElement) || !trigger.dataset.handle) return;

    // BCRS fee: surface the fee variant id from the cart row so the subscription
    // drawer can attach the fee when converting OTP → subscription.
    const lineRow = trigger.closest("tr[data-key]");
    const bcrsFeeVariantId = lineRow instanceof HTMLElement ? lineRow.dataset.bcrsFeeVariantId : undefined;

    document.dispatchEvent(
      new SubscriptionOpenEvent({
        handle: trigger.dataset.handle,
        variantId: trigger.dataset.variantId,
        mode: "convert",
        lineKey: trigger.dataset.lineKey,
        quantity: Number(trigger.dataset.quantity) || 1,
        bcrsFeeVariantId,
      })
    );
  }

  /**
   * Saves a cart line to the customer's Swish "Saved for Later" list, then removes it from
   * the cart. Wired from the line's "Save for later" CTA via `on:click="/saveForLater"`.
   *
   * Login-gated (MA save-for-later spec): guests get a brief notice and are redirected to
   * the login page with a `return_to` back to the current page. On a successful Swish add
   * the line is removed via the shared removal path (onLineItemRemove → /cart/change.js →
   * section morph), so the cart/drawer updates instantly with no page refresh.
   *
   * @param {Event} event - The click event; its target carries the line data attributes.
   */
  async saveForLater(event) {
    const trigger = event.target instanceof Element ? event.target.closest(".cart-items__save-later") : null;
    if (!(trigger instanceof HTMLElement)) return;

    // Re-entrancy guard — set SYNCHRONOUSLY before any `await`. Without this, a fast second
    // click (the button isn't disabled until after the dynamic imports resolve) starts a
    // second save that also calls onLineItemRemove(line); removing the same line index twice
    // deletes the item that shifted into it — i.e. a second, unintended cart item is removed.
    if (trigger.hasAttribute("disabled")) return;
    trigger.setAttribute("disabled", "");

    const line = Number(trigger.dataset.line);
    const { productId, variantId } = trigger.dataset;
    if (!line || !productId || !variantId) {
      trigger.removeAttribute("disabled");
      return;
    }

    const [{ isLoggedIn, addToSavedForLater }, { showSwishNotice, suppressExternalNotices }] = await Promise.all([
      import("@theme/swish-client"),
      import("@theme/swish-notice"),
    ]);

    // Not logged in → notify, then send to login. `account_login_redirect_url` (MA-1619)
    // fixes the post-login destination at /pages/account for every sign-in entry point in
    // the theme, rather than returning to the current page. The short delay lets the notice
    // register before navigation starts.
    if (!isLoggedIn()) {
      showSwishNotice(Theme.translations.save_for_later_login_required, { duration: 0 });
      const loginUrl =
        Theme.routes.account_login_redirect_url ||
        Theme.routes.storefront_login_url ||
        Theme.routes.account_login_url ||
        "/account/login";
      setTimeout(() => {
        window.location.href = loginUrl;
      }, 1200);
      return;
    }

    trigger.setAttribute("disabled", "");
    // Show the spinner while the Swish add is in flight (mirrors the card "Add" loading state).
    trigger.classList.add("cart-items__save-later--loading");

    try {
      // No confirmation toast on save (per spec) — also suppress Swish's own "saved" toast.
      suppressExternalNotices();
      await addToSavedForLater({ productId, variantId, quantity: 1 });

      // Remove the saved line from the cart. When it's the LAST line, use onLineItemRemove so
      // the empty-cart state renders (an open drawer doesn't reliably morph to empty). When
      // OTHER lines remain, morph straight from the server instead: onLineItemRemove's deferred
      // optimistic row.remove() (fired on animation-end) can land AFTER morphSection repurposes
      // that DOM node for the next line, deleting a still-in-cart sibling from the drawer (it
      // reappears on refresh). The async add above widens that race window.
      const remainingRows = this.refs.cartItemRows || [];
      if (remainingRows.length <= 1) {
        this.onLineItemRemove(line);
      } else {
        this.updateQuantity({ line, quantity: 0, action: "clear" });
      }
    } catch (error) {
      console.error("[swish] save for later failed", error);
      trigger.removeAttribute("disabled");
      trigger.classList.remove("cart-items__save-later--loading");
      showSwishNotice(Theme.translations.save_for_later_error, { variant: "error" });
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector("text-component"));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.#captureRemoval(line);

    this.updateQuantity({
      line,
      quantity: 0,
      action: "clear",
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter(row => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // BCRS fee: if removing a main product, also visually remove the fee paired
    // to THIS line (variant + selling plan) — not a sibling line's fee.
    const mainVariantId = cartItemRowToRemove.dataset.variantId;
    if (mainVariantId && !cartItemRowToRemove.dataset.isBcrsFee) {
      const feeRow = findPairedFeeRow(this, mainVariantId, cartItemRowToRemove.dataset.sellingPlan || "");
      if (feeRow && !rowsToRemove.includes(feeRow)) rowsToRemove.push(feeRow);
    }

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById("empty-cart-template");
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      // The template is captured at page load, so on a cart that had items it holds a
      // populated cross-sell carousel. Those cards are stale and belong to the filled
      // cart — drop them rather than flash them inside the empty-cart layout.
      clone.querySelectorAll('[data-cart-recs="cross-sell"]').forEach(el => el.remove());

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? "empty-cart-drawer" : "empty-cart-page"]);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach(row => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty("--row-height", `${row.clientHeight}px`);
      row.classList.add("removing");

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;

    // BCRS fee: resolve the changed main row (fee rows are re-parented under
    // their main line, so positional lookup in cartItemRows is unreliable) and
    // its paired fee line. The fee quantity is the SUM of every line of the
    // main variant — the same variant can be split across lines (differing
    // properties / selling plans), and copying just the changed line's
    // quantity would overwrite the fee for the others.
    const mainRow =
      this.querySelector(`tr[data-line="${line}"]:not([data-is-bcrs-fee])`) || this.refs.cartItemRows[line - 1];
    const mainKey = mainRow?.dataset.key;
    const mainVariantId = mainRow?.dataset.variantId;
    let feeKey = null;
    let feeQuantity = 0;
    if (mainVariantId && !mainRow?.dataset.isBcrsFee) {
      // Per-line pairing: the fee for THIS parent line (variant + selling plan)
      // mirrors THIS line's new quantity — sibling lines of the same variant
      // (e.g. an OTP vs a subscription) have their own separate fee lines.
      const feeRow = findPairedFeeRow(this, mainVariantId, mainRow?.dataset.sellingPlan || "");
      feeKey = feeRow?.dataset.key || null;
      if (feeKey) feeQuantity = quantity;
    }

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach(item => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    const sections = Array.from(sectionsToUpdate).join(",");

    // BCRS fee: when a fee line is paired and this isn't a selling-plan change,
    // change the main line AND the fee line in ONE atomic /cart/update.js call
    // (keyed by line item key) — no sequential change.js calls, so the pair
    // can't diverge from a half-applied removal/quantity change. Selling-plan
    // changes can't go through update.js, so they keep the change.js path with
    // the fee synced first (fail closed), and reconciliation heals any drift.
    let requestUrl;
    let body;
    let feeSync = Promise.resolve();
    if (feeKey && mainKey && config.sellingPlan == null) {
      requestUrl = window.Theme?.routes?.cart_update_url || "/cart/update.js";
      body = JSON.stringify({
        updates: { [mainKey]: quantity, [feeKey]: feeQuantity },
        sections,
        sections_url: window.location.pathname,
      });
    } else {
      requestUrl = `${Theme.routes.cart_change_url}`;
      body = JSON.stringify({
        ...(mainKey ? { id: mainKey } : { line: line }),
        quantity: quantity,
        ...(config.sellingPlan != null ? { selling_plan: config.sellingPlan } : {}),
        sections,
        sections_url: window.location.pathname,
      });
      if (feeKey && config.sellingPlan != null) {
        // The fee stays a one-time deposit — sync only its quantity, never the
        // selling plan (the fee product has none; sending one errors the cart).
        feeSync = silentCartChange({ id: feeKey, quantity: feeQuantity });
      }
    }

    cartTotal?.shimmer();

    feeSync
      .then(() => fetch(requestUrl, fetchConfig("json", { body })))
      .then(response => {
        return response.text();
      })
      .then(responseText => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          // The line is still in the cart — a failed removal is not a removal.
          this.#pendingRemovals.delete(line);
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        if (config.action === "clear") this.#reportRemoval(line);

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          "text/html"
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: "cart-items-component",
            sections: parsedResponseText.sections,
          })
        );

        // On the cart page the header count lives in a separate section that this
        // morph never re-renders (only the drawer's hydration morph refreshes it),
        // so sync the header count element(s) directly from the fresh count.
        if (newCartHiddenItemCount != null) {
          for (const el of document.querySelectorAll('[data-hydration-key="cart-item-count"]')) {
            el.textContent = String(newCartItemCount);
          }
        }

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? "hydration" : "full");

        this.#updateCartQuantitySelectorButtonStates();

        // BCRS fee: after morph, regroup fee rows under their parent lines and
        // detect orphan fee lines whose parent product was removed from the
        // cart (e.g. via a race or cross-tab change), cleaning them up silently.
        reorderFeeRows(this);
        this.#reconcileFees();
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = event => {
    this.#handleCartUpdate(event);
  };

  /**
   * Snapshots a line's identity for `ng_remove_from_cart` while its row is
   * still rendered. Overwrites any earlier record for the same line, so a
   * removal whose request never resolved cannot be reported with stale data.
   * @param {number} line - The line item index.
   */
  #captureRemoval(line) {
    const row = this.querySelector(`tr[data-line="${line}"]`) || this.refs.cartItemRows[line - 1];
    if (!row) return;

    // The line-item <tr> carries no product id (adding one would make every
    // cart row match the product-card selector in analytics.js); the
    // save-for-later button inside it does.
    const identity = row.querySelector("[data-product-id]");

    this.#pendingRemovals.set(line, {
      productId: identity?.dataset.productId || "",
      productHandle: identity?.dataset.productHandle || "",
      productTitle: textParam(row.querySelector(".cart-items__title")?.textContent),
      variantId: row.dataset.variantId || "",
      lineItemKey: row.dataset.key || "",
      quantity: Number(row.dataset.quantity) || 0,
      sellingPlanId: row.dataset.sellingPlan || "",
      cartSource: this.isDrawer ? "cart-drawer" : "cart-page",
    });
  }

  /**
   * Emits `ng_remove_from_cart` for a removal the server has confirmed.
   * @param {number} line - The line item index.
   */
  #reportRemoval(line) {
    const payload = this.#pendingRemovals.get(line);
    if (!payload) return;

    this.#pendingRemovals.delete(line);
    track(ANALYTICS_EVENTS.REMOVE_FROM_CART, payload);
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector("input");

    if (!quantityInput) throw new Error("Quantity input not found");

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = event => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer
        .renderSection(this.sectionId, { cache: false })
        .then(() => reorderFeeRows(this))
        .catch(() => {});
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();

      // BCRS fee: after an external cart mutation (product-form, subscription-drawer,
      // or another tab) morphs the DOM, regroup fee rows under their parent lines,
      // then detect orphan fee lines whose parent was removed and clean them up.
      reorderFeeRows(this);
      this.#reconcileFees();
    } else if (this.isDrawer) {
      // No section markup on the event (e.g. an item added from the empty-state
      // best sellers while the drawer is open). Morphing an OPEN <dialog> does
      // not reliably swap the empty state for the populated cart, so re-render
      // the section (to update out-of-dialog UI like the header count) and then
      // replace the drawer contents with the freshly rendered markup.
      this.#refreshDrawerContents();
    } else {
      sectionRenderer
        .renderSection(this.sectionId, { cache: false })
        .then(() => reorderFeeRows(this))
        .catch(() => {});
    }
  };

  /**
   * Re-renders the cart drawer contents from the Section Rendering API.
   *
   * Used when the drawer is open and the cart changes from an event that carries
   * no section HTML; a direct replace avoids the open-dialog morph limitation.
   */
  async #refreshDrawerContents() {
    try {
      const sectionHTML = await sectionRenderer.renderSection(this.sectionId, { cache: false });
      const freshComponent = new DOMParser()
        .parseFromString(sectionHTML, "text/html")
        .querySelector("cart-items-component[data-drawer]");

      if (freshComponent) {
        this.replaceChildren(...freshComponent.childNodes);
        this.#updateCartQuantitySelectorButtonStates();

        // BCRS fee: the fresh markup renders fee rows hidden — regroup them
        // beneath their parents (which also reveals them) and clean orphans.
        reorderFeeRows(this);
        this.#reconcileFees();
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector("input[data-cart-quantity]");
        if (!input) continue;

        input.setAttribute("data-cart-quantity", item.quantity.toString());

        // Update the quantity selector's internal state
        if ("updateCartQuantity" in selector && typeof selector.updateCartQuantity === "function") {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll("cart-quantity-selector-component")) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
